const ALLOWED_PROTOCOLS = new Set(["http:", "https:"]);

/** Product URLs: the host is discarded, not compared — Bloomreach's indexed
 *  `url` is always canonicalized to the production domain regardless of which
 *  environment (dusk.com, duskretail.myshopify.com, duskstaging.myshopify.com,
 *  a *.shopifypreview.com preview, or localhost) queried it, so requiring the
 *  host to match rejects every product on every environment but production.
 *  Discarding the host (instead of requiring it to match) stays safe: the
 *  return value is always a bare path, so it can never navigate off-site
 *  regardless of what host the input URL carried. */
export function toSafeInternalPath(raw: string): string | null {
  try {
    const url = new URL(raw, window.location.origin);
    if (!ALLOWED_PROTOCOLS.has(url.protocol)) return null;
    if (url.username || url.password) return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}

// Hardcoded, NOT window.location-relative: a Bloomreach keyword-redirect
// target is business-configured against production regardless of which
// environment (preview theme, staging, prod) is running the query — a
// dynamic check would reject every valid redirect on non-prod environments.
const REDIRECT_BASE_DOMAIN = "dusk.com";

function isDuskDomain(hostname: string): boolean {
  // the leading "." matters: without it, "evildusk.com"/"notdusk.com" would
  // incorrectly pass a bare .endsWith("dusk.com") check.
  return hostname === REDIRECT_BASE_DOMAIN || hostname.endsWith("." + REDIRECT_BASE_DOMAIN);
}

// Bloomreach's documented redirect shape has neither a scheme nor a leading "//"
// (e.g. "www.example.com/path", "help.dusk.com/support") — WHATWG URL parsing treats
// a string like that, passed as the first arg to `new URL(raw, base)`, as a PATH
// relative to `base`, not as its own host: `new URL("help.dusk.com/x", "https://dusk.com")`
// resolves to "https://dusk.com/help.dusk.com/x", silently nesting the intended subdomain
// under dusk.com's root instead of navigating there. Prepending "https://" before parsing
// makes the bare shape resolve to its real host. Only skip that prepend when the raw
// value already looks absolute (an explicit scheme, or a leading "//"), so a full URL or
// protocol-relative URL — safe or malicious — still resolves to its own real host too.
function normalizeRedirectTarget(raw: string): string {
  const hasExplicitScheme = /^[a-z][a-z0-9+.-]*:/i.test(raw);
  const isProtocolRelative = raw.startsWith("//");
  return hasExplicitScheme || isProtocolRelative ? raw : `https://${raw}`;
}

/** Keyword redirects: any dusk.com subdomain (help.dusk.com, www.dusk.com, bare dusk.com). */
export function toSafeRedirectUrl(raw: string): string | null {
  try {
    const url = new URL(normalizeRedirectTarget(raw), `https://${REDIRECT_BASE_DOMAIN}`);
    if (!ALLOWED_PROTOCOLS.has(url.protocol)) return null;
    if (url.username || url.password) return null;
    if (!isDuskDomain(url.hostname)) return null;
    return url.toString();
  } catch {
    return null;
  }
}
